<template>
  <v-dialog max-width="500" :value="show" @input="closeModal">
    <v-card class="comment-modal">
      <v-card-title class="modal-header">
        <h3 class="modal-title">{{ $t('activities.comment') }}</h3>
      </v-card-title>

      <v-card-content class="modal-content">
        <div v-if="loading" class="loading-container">
          <p class="loading-text">{{ $t('activities.loading') }}</p>
        </div>

        <div v-else-if="error" class="error-container">
          <p class="error-text">{{ error }}</p>
          <v-btn @click="loadComments" color="primary" text small>
            {{ $t('activities.try_again') }}
          </v-btn>
        </div>

        <div v-else-if="comments.length === 0" class="empty-state">
          <p>{{ $t('activities.no_data') }}</p>
        </div>

        <div v-else class="comments-list">
          <div v-for="comment in comments" :key="comment.id" class="comment-item">
            <div class="comment-info">
              <div class="comment-author">{{ comment.created_by.full_name || 'Невідомий автор' }}</div>
              <div class="comment-date">{{ formatDate(comment.created_at) }}</div>
            </div>

            <div class="comment-text">
              {{ comment.description }}
            </div>
          </div>
        </div>


        <div class="comment-actions">
          <button v-if="showEditButton && !loading" @click="openAddComment" class="edit-comment-btn">
            {{ $t('activities.edit_comment') }}
          </button>
          <delete-btn @delete="onDeleteComment" class="delete-comment-btn">
          </delete-btn>
        </div>
      </v-card-content>
    </v-card>
  </v-dialog>
</template>

<script>
import api from '@/api'
import DeleteBtn from '@/components/common/delete-btn/DeleteBtn'
export default {
  name: 'CommentModal',
  components: { DeleteBtn },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    client: {
      type: Object,
      default: null
    },
    farm: {
      type: Object,
      default: null
    },
    showEditButton: {
      type: Boolean,
      default: true
    }
  },

  data () {
    return {
      loading: false,
      error: null,
      comments: []
    }
  },

  computed: {
    latestComment () {
      if (this.comments.length === 0) return null
      return this.comments[this.comments.length - 1]
    },
    latestCommentId () {
      return this.latestComment ? this.latestComment.id : null
    },
    hasComments () {
      return this.comments.length > 0
    }
  },

  watch: {
    show (newVal) {
      if (newVal && this.client.id) {
        this.loadComments()
      }
    }
  },

  mounted () {
    document.addEventListener('keydown', this.handleEscapeKey)
  },

  beforeDestroy () {
    document.removeEventListener('keydown', this.handleEscapeKey)
  },

  methods: {
    async onDeleteComment () {
      try {
        if (!this.client || !this.client.id) {
          console.error('No client specified for comment deletion')
          return
        }

        if (!this.latestCommentId) {
          console.error('No comment ID found')
          this.$toasted.error('Не вдалося знайти ID коментаря')
          return
        }

        await api.dashboard.groups.deleteClientComment(this.client.id, this.latestCommentId)

        this.$toasted.show('Коментар успішно видалено')

        this.$emit('comment-deleted', {
          clientId: this.client.id,
          farmId: this.farm && this.farm.id ? this.farm.id : null,
          commentId: this.latestCommentId
        })
        this.$emit('close')
      } catch (error) {
        console.error('Failed to delete comment:', error)
        this.$toasted.error('Помилка при видаленні коментаря')
      }
    },
    async loadComments () {
      if (!this.client.id) {
        return
      }

      this.loading = true
      this.error = null
      const payload = {}

      if (this.farm) {
        payload.farm = this.farm.id
      }

      try {
        const res = await api.dashboard.groups.getClientComments(this.client.id, payload)

        if (Array.isArray(res)) {
          this.comments = res
        } else if (res.data && Array.isArray(res.data)) {
          this.comments = res.data
        } else if (res.data) {
          this.comments = [res.data]
        } else {
          this.comments = []
        }
      } catch (error) {
        console.error('Failed to load client comments:', error)
        this.comments = []
      } finally {
        this.loading = false
      }
    },

    formatDate (dateString) {
      const date = new Date(dateString)
      const time = date.toLocaleTimeString('uk-UA', {
        hour: '2-digit',
        minute: '2-digit'
      })
      const dateFormatted = date.toLocaleDateString('uk-UA', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      return `${time} / ${dateFormatted}`
    },

    closeModal () {
      this.$emit('close')
    },

    openAddComment () {
      this.$emit('add-comment', {
        existingComment: this.latestComment
      })
    },

    handleEscapeKey (event) {
      if (event.key === 'Escape' && this.show) {
        this.closeModal()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.v-card__title {
  border-bottom: 0.1rem solid #d7d7d7;
  padding-bottom: 2rem;
}

.comment-actions {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.comment-modal {
  .modal-header {
    padding: 2rem 2.4rem 1.6rem !important;
    border-bottom: 0.1rem solid #e0e0e0;
    display: flex;
    justify-content: space-between;
    align-items: center;

    .modal-title {
      font-size: 1.8rem;
      font-weight: 600;
      color: #333;
      margin: 0;
    }
  }

  .modal-content {
    padding: 2.4rem !important;
  }
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 0;

  .loading-text {
    margin-top: 1rem;
    color: #666;
    font-size: 1.4rem;
  }
}

.error-container {
  text-align: center;
  padding: 2rem 0;

  .error-text {
    color: #d32f2f;
    margin-bottom: 1rem;
    font-size: 1.4rem;
  }
}

.empty-state {
  text-align: center;
  padding: 2rem 0;
  color: #666;
  font-size: 1.4rem;
}

.comments-list {
  max-height: 300px;
  overflow-y: auto;
  margin-bottom: 2rem;
}

.comment-item {
  margin-bottom: 2rem;
  padding-bottom: 2rem;
  border-bottom: 1px solid #eee;

  &:last-child {
    margin-bottom: 0;
    padding-bottom: 0;
    border-bottom: none;
  }
}

.comment-info {
  margin-bottom: 1.2rem;

  .comment-author {
    font-size: 1.6rem;
    font-weight: 600;
    color: #333;
    margin-bottom: 0.4rem;
  }

  .comment-date {
    font-size: 1.4rem;
    color: #999;
    font-weight: 400;
  }
}

.comment-text {
  color: #333;
  line-height: 1.6;
  font-size: 1.5rem;
  background: #f8f9fa;
  padding: 1.6rem;
  border-radius: 1rem;
  word-wrap: break-word;
}

.edit-comment-btn {
  width: 100%;
  background: #4CAF50;
  color: white;
  border: none;
  padding: 1.2rem 2.4rem;
  border-radius: 0.8rem;
  font-size: 1.6rem;
  font-weight: 500;
  cursor: pointer;

  &:hover {
    background: #1fb348;
  }

  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
  }
}

@media (max-width: 48rem) {
  .comment-modal {
    .modal-header {
      padding: 1.6rem 2rem 1.2rem !important;

      .modal-title {
        font-size: 1.6rem;
      }
    }

    .modal-content {
      padding: 2rem !important;
    }
  }

  .comment-info {
    .comment-author {
      font-size: 1.5rem;
    }

    .comment-date {
      font-size: 1.3rem;
    }
  }

  .comment-text {
    font-size: 1.4rem;
    padding: 1.2rem;
  }

  .edit-comment-btn {
    padding: 1rem 2rem;
    font-size: 1.5rem;
  }

  .loading-text,
  .error-text,
  .empty-state {
    font-size: 1.3rem;
  }
}
</style>